import { useTranslations } from "use-intl";

import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Link } from "@/components/link";
import { buildEditorialHubPath } from "@/features/editorial-hub/editorial-hub-path";
import { cn } from "@/lib/utils";
import { buildFeaturePageLandingPath } from "@garimpa/lib/buildFeaturePageLandingPath";
import { type FeaturePageFamily } from "@garimpa/lib/featurePage";
import { type ViewerLocale } from "@garimpa/lib/viewerPreferences";

const DEFAULT_BREADCRUMB_STYLES = {
  separator: "text-muted-foreground",
  link: "text-muted-foreground hover:text-primary",
  current: "text-primary",
} as const;

const OVERLAY_BREADCRUMB_STYLES = {
  separator: "text-white/60",
  link: "text-white/75 drop-shadow-sm hover:text-white",
  current: "text-white drop-shadow-sm",
} as const;

export function EditorialBreadcrumbs({
  family,
  locale,
  currentTitle,
  className,
  overlay = false,
}: {
  family: FeaturePageFamily;
  locale: ViewerLocale;
  currentTitle?: string;
  className?: string;
  overlay?: boolean;
}) {
  const t = useTranslations("featurePageLanding");
  const familyTitle = t(`${family}.title`);
  const styles = overlay ? OVERLAY_BREADCRUMB_STYLES : DEFAULT_BREADCRUMB_STYLES;

  return (
    <Breadcrumbs
      className={cn("mb-8", className)}
      label={t("breadcrumbLabel")}
      separatorClassName={styles.separator}
    >
      <Link to={buildEditorialHubPath(locale)} className={cn("transition-colors", styles.link)}>
        {t("editorial")}
      </Link>
      {currentTitle ? (
        <Link
          to={buildFeaturePageLandingPath(locale, family)}
          className={cn("transition-colors", styles.link)}
        >
          {familyTitle}
        </Link>
      ) : (
        <span className={cn("font-medium", styles.current)}>{familyTitle}</span>
      )}
      {currentTitle ? (
        <span
          className={cn("max-w-full truncate font-medium", styles.current)}
          title={currentTitle}
        >
          {currentTitle}
        </span>
      ) : null}
    </Breadcrumbs>
  );
}
